/**
 * SettingsCourierApi Component - Updated with Navigation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import editIcon from '../assets/Edit (1).svg';
import deleteIcon from '../assets/deleteBtn.svg';
import { courierCredentialApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

// Shipping mode options
const SHIPPING_MODE_OPTIONS = ['SURFACE', 'EXPRESS'];

function Toggle({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 border-2`}
      style={{
        backgroundColor: checked ? currentTheme.color : '#FFFFFF',
        borderColor: isDark ? '#FFFFFF' : '#D1D5DB',
        '--tw-ring-color': currentTheme.color
      }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full transition-transform ${checked ? 'translate-x-5 bg-white' : 'translate-x-1 bg-gray-400'
          } border-2 border-white shadow-sm`}
      />
    </button>
  );
}

export default function SettingsCourierApi() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate(); // Add this hook
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const credentialsData = await courierCredentialApi.getAllCredentials();

      const transformedRows = credentialsData.map((item) => ({
        id: item.id.toString(),
        campaignName: item.campaignName,
        courierType: item.courierType,
        apiEmail: item.apiEmail,
        apiPassword: item.apiPassword,
        shippingMode: item.shippingMode,
        apiToken: item.apiToken || '-',
        status: item.status,
        height: `${item.orderHeight} CM`,
        width: `${item.orderWidth} CM`,
        length: `${item.orderLength} CM`,
        weight: `${item.orderWeight} GM`,
      }));

      setRows(transformedRows);
    } catch (err) {
      console.error('Error fetching courier credentials:', err);
      setError(err.message || err.error || 'Failed to load courier credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInlineUpdate = async (rowId, patch) => {
    try {
      const row = rows.find(r => r.id === rowId);
      if (!row) return;

      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));

      const apiData = {
        campaignName: row.campaignName,
        courierType: row.courierType,
        apiEmail: row.apiEmail,
        apiPassword: row.apiPassword,
        shippingMode: patch.shippingMode || row.shippingMode,
        orderHeight: parseInt(row.height.replace(' CM', '')),
        orderWidth: parseInt(row.width.replace(' CM', '')),
        orderLength: parseInt(row.length.replace(' CM', '')),
        orderWeight: parseInt(row.weight.replace(' GM', '')),
        status: patch.status !== undefined ? patch.status : row.status,
      };

      await courierCredentialApi.updateCredential(rowId, apiData);
    } catch (err) {
      console.error('Error updating courier credential:', err);
      await fetchData();
      message.error('Failed to update. Please try again.');
    }
  };

  const handleDelete = async (rowId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"?`)) {
      return;
    }

    try {
      await courierCredentialApi.deleteCredential(rowId);
      await fetchData();
      message.success('Courier credential deleted successfully!');
    } catch (err) {
      console.error('Error deleting courier credential:', err);
      message.error(err.message || err.error || 'Failed to delete courier credential. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading courier credentials...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={fetchData}
          className="px-6 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
          style={{ backgroundColor: currentTheme.color }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 relative min-h-screen -mx-8 px-8 pb-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : ''}`}>
      <div className="sticky top-0 z-30 -mx-8 px-8 py-3" style={{ backgroundColor: `${currentTheme.color}10` }}>
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Courier API</h2>

          {/* Navigate to Add Courier Credential page */}
          <button
            type="button"
            onClick={() => navigate('/settings/courier-api/add')} // Update this route as needed
            className="inline-flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg hover:opacity-90 transition-all active:scale-95"
            style={{ backgroundColor: currentTheme.color }}
            title="Add Courier API"
            aria-label="Add Courier API"
          >
            <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8.5 8.5L5 12l3.5 3.5M15.5 8.5L19 12l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 7l-2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M20 19v-3m-1.5 1.5h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className={`${isDark ? 'bg-[#1e293b]/80 border-white/10' : 'bg-white/80 border-white/50'} backdrop-blur rounded-2xl shadow-xl border overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full">
            <thead>
              <tr className="text-white" style={{ backgroundColor: currentTheme.color }}>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Campaign Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">API Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">API Password</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Shipping Mode</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">API Token</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Height</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Width</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Length</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Weight</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Action</th>
              </tr>
            </thead>

            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-200'}`}>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : ''} style={{ backgroundColor: idx % 2 === 0 ? undefined : `${currentTheme.color}08` }}>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.campaignName}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.apiEmail}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.apiPassword}</td>
                  <td className="px-4 py-3">
                    <select
                      value={r.shippingMode}
                      onChange={(e) => handleInlineUpdate(r.id, { shippingMode: e.target.value })}
                      className={`w-[120px] h-9 px-3 border rounded-lg text-sm transition-all focus:outline-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ focusBorderColor: currentTheme.color }}
                    >
                      {SHIPPING_MODE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap max-w-[200px] truncate ${isDark ? 'text-gray-400' : 'text-gray-900'}`} title={r.apiToken}>
                    {r.apiToken}
                  </td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={r.status}
                      onChange={(v) => handleInlineUpdate(r.id, { status: v })}
                      ariaLabel={`Toggle status for ${r.campaignName}`}
                    />
                  </td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.height}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.width}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.length}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.weight}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => message.info(`Edit functionality - Navigate to edit page for ${r.campaignName}`)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-blue-800 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40' : 'border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" className="w-5 h-5" style={{ filter: isDark ? 'invert(1) brightness(2)' : 'none' }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id, r.campaignName)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-red-800 bg-red-900/20 text-red-400 hover:bg-red-900/40' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                        title="Delete"
                      >
                        <img src={deleteIcon} alt="Delete" className="w-5 h-5" style={{ filter: isDark ? 'invert(1) brightness(2)' : 'none' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}